import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle2, Clock, Circle, Loader2, Copy, Check, ExternalLink,
  Wallet, History, AlertCircle, ArrowLeft,
} from 'lucide-react';

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

// Map backend funding status -> which timeline steps are complete
const STEP_STATE = {
  created: 1, proof_submitted: 1, pending_verification: 1,
  under_review: 2, verified: 2, admin_review: 2,
  approved: 3, credited: 4, rejected: 1, expired: 1,
};

const isValidTronHash = (h) => {
  if (!h) return false;
  const hx = h.toLowerCase().startsWith('0x') ? h.slice(2) : h;
  return hx.length === 64 && /^[0-9a-fA-F]+$/.test(hx);
};

export default function RechargeSuccessPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fundingId = params.get('funding_id');

  const [funding, setFunding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!fundingId) { setError('missing'); setLoading(false); return; }
    try {
      setLoading(true);
      const res = await api.get(`/funding/requests/${fundingId}`);
      setFunding(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.status === 404 ? 'notfound' : 'error');
    } finally {
      setLoading(false);
    }
  }, [api, fundingId]);

  useEffect(() => { load(); }, [load]);

  const copyHash = async () => {
    try {
      await navigator.clipboard.writeText(funding.transaction_hash);
      setCopied(true);
      toast.success('Transaction hash copied');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96" data-testid="recharge-success-loading"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto" data-testid="recharge-success-error">
        <Card className="border-red-500/30">
          <CardContent className="py-12 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-red-500/15 flex items-center justify-center mx-auto"><AlertCircle className="w-7 h-7 text-red-500" /></div>
            <h1 className="text-2xl font-heading font-bold">
              {error === 'notfound' ? 'Funding Request Not Found' : error === 'missing' ? 'No Funding Reference' : 'Something Went Wrong'}
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              {error === 'notfound'
                ? 'We could not find this funding request for your account.'
                : 'We could not load your funding request details right now.'}
            </p>
            <div className="flex gap-3 justify-center">
              {error === 'error' && <Button onClick={load} data-testid="success-retry-btn">Try Again</Button>}
              <Button variant="outline" onClick={() => navigate('/dashboard/wallet/recharge')} data-testid="success-back-recharge"><ArrowLeft className="w-4 h-4 mr-2" />Back to Recharge</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = funding.status;
  const stepsDone = STEP_STATE[status] ?? 1;
  const rejected = status === 'rejected';
  const credited = status === 'credited' || status === 'approved';
  const hash = funding.transaction_hash;
  const steps = [
    { label: 'Request Submitted', desc: 'Your funding request has been received' },
    { label: 'Blockchain Verification', desc: 'Verifying the transfer on the TRON (TRC20) network' },
    { label: 'Review', desc: 'AwadhPay reviews and approves the funding' },
    { label: 'Wallet Credit', desc: 'INR balance credited to your wallet' },
  ];

  const statusBadge = rejected
    ? { cls: 'bg-red-500/15 text-red-500 border-red-500/30', text: 'Rejected' }
    : credited
      ? { cls: 'bg-green-500/15 text-green-600 border-green-500/30', text: 'Credited' }
      : { cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30', text: 'Verification Pending' };

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-10" data-testid="recharge-success-page">
      {/* Hero */}
      <div className="text-center space-y-3 pt-2">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${rejected ? 'bg-red-500/15' : 'bg-green-500/15'}`}>
          {rejected ? <AlertCircle className="w-9 h-9 text-red-500" /> : <CheckCircle2 className="w-9 h-9 text-green-500" data-testid="success-check-icon" />}
        </div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold">
          {rejected ? 'Funding Request Rejected' : 'Transaction Submitted Successfully'}
        </h1>
        <p className="text-muted-foreground max-w-lg mx-auto text-sm">
          {rejected
            ? (funding.rejection_reason || 'This funding request was rejected. Please contact support or submit a new request.')
            : 'Your USDT funding request has been submitted for verification. Our system will verify the transaction on the TRON (TRC20) network. Once verified and approved, the corresponding INR balance will be credited to your AwadhPay wallet.'}
        </p>
      </div>

      {/* Details card */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold">Transaction Summary</h2>
            <Badge variant="outline" className={`rounded-sm ${statusBadge.cls}`} data-testid="success-status-badge">{statusBadge.text}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Row label="Funding ID" value={<span className="font-mono font-semibold text-primary" data-testid="success-funding-id">{funding.funding_id}</span>} />
            <Row label="MID" value={<span className="font-mono">{funding.mid || '—'}</span>} />
            <Row label="USDT Amount" value={<span className="font-mono">{funding.usdt_amount} USDT</span>} />
            <Row label="Exchange Rate" value={<span className="font-mono">₹{funding.exchange_rate} / USDT</span>} />
            <Row label="Network" value="TRON (TRC20)" />
            <Row label="Expected Credit" value={<span className="font-mono text-blue-600" data-testid="success-expected-inr">{fmtINR(funding.net_credit || funding.inr_amount)}</span>} />
          </div>

          {/* Expected vs actual distinction */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm">
            <span className="text-muted-foreground">Wallet Credit</span>
            <span className="font-medium text-amber-600" data-testid="success-wallet-credit-status">{credited ? 'Credited' : 'Pending Verification'}</span>
          </div>

          {/* TX hash / proof */}
          {hash ? (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Transaction Hash</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-muted/50 rounded px-2 py-1.5 break-all" data-testid="success-tx-hash">{hash}</code>
                <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={copyHash} data-testid="success-copy-hash">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                {isValidTronHash(hash) && (
                  <a href={`https://tronscan.org/#/transaction/${hash.toLowerCase().startsWith('0x') ? hash.slice(2) : hash}`} target="_blank" rel="noreferrer" data-testid="success-explorer-link">
                    <Button variant="outline" size="icon" className="h-8 w-8 shrink-0"><ExternalLink className="w-4 h-4" /></Button>
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground" data-testid="success-proof-note">Payment proof submitted</div>
          )}
        </CardContent>
      </Card>

      {/* Verification timeline */}
      <Card>
        <CardContent className="p-5">
          <h2 className="font-heading font-semibold mb-4">Verification Progress</h2>
          <div className="space-y-0" data-testid="success-timeline">
            {steps.map((s, i) => {
              const done = i < stepsDone && !rejected;
              const current = i === stepsDone && !rejected;
              return (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    {done ? <CheckCircle2 className="w-5 h-5 text-green-500" />
                      : current ? <Clock className="w-5 h-5 text-amber-500" />
                      : <Circle className="w-5 h-5 text-muted-foreground/40" />}
                    {i < steps.length - 1 && <div className={`w-0.5 flex-1 my-1 ${done ? 'bg-green-500/40' : 'bg-border'}`} style={{ minHeight: 24 }} />}
                  </div>
                  <div className="pb-4">
                    <p className={`text-sm font-medium ${done ? 'text-foreground' : current ? 'text-amber-600' : 'text-muted-foreground'}`}>{s.label}</p>
                    <p className="text-xs text-muted-foreground">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" className="flex-1" onClick={() => navigate('/dashboard/wallet/recharge')} data-testid="success-funding-history-btn">
          <History className="w-4 h-4 mr-2" />View Funding History
        </Button>
        <Button className="flex-1" onClick={() => navigate('/dashboard/wallet')} data-testid="success-go-wallet-btn">
          <Wallet className="w-4 h-4 mr-2" />Go to Wallet
        </Button>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-0.5">{value}</p>
  </div>
);
